import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMountTransition } from '../hooks/useMountTransition';
import { useSheetSwipeDismiss } from '../hooks/useSheetSwipeDismiss';
import {
  clearNotifications,
  formatNotifTime,
  markAllRead,
  markRead,
  noticeIconClass,
  noticeToneClass,
  openNoticeLink,
} from '../utils/notifications';
import { showToast } from '../utils/toast';
import { activateClose, dismissFocus, suppressGhostClick } from '../utils/dismissFocus';
import { LINKS } from '../constants/links';
import NoticeDialog from './NoticeDialog';

const MOBILE_MQ = '(max-width: 767px)';

const NotificationsPanel = ({ open, onClose, notifications, setNotifications, anchorRef }) => {
  const { mounted, active } = useMountTransition(open, 320);
  const [pos, setPos] = useState({ top: 72, right: 12 });
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const [opened, setOpened] = useState(null);
  const sheetRef = useRef(null);
  const handleRef = useRef(null);
  const backdropRef = useRef(null);

  const handleClose = useCallback(
    (event) => {
      dismissFocus(event);
      suppressGhostClick(420);
      onClose?.();
    },
    [onClose]
  );

  const swipeClose = useCallback(() => {
    suppressGhostClick(420);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);

  useSheetSwipeDismiss({
    sheetRef,
    handleRef,
    backdropRef,
    onDismiss: swipeClose,
    enabled: mounted && active && isMobile,
  });

  useEffect(() => {
    if (!mounted) return undefined;

    document.body.classList.add('kkt-dialog-open');

    const update = () => {
      const mobile = window.matchMedia(MOBILE_MQ).matches;
      setIsMobile(mobile);
      const el = anchorRef?.current;
      const fallback = {
        top: Math.max(16, Math.round(mobile ? 12 : 72)),
        right: 12,
      };
      if (!el) {
        setPos(fallback);
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width < 8 || rect.height < 8) {
        setPos({
          top: Math.max(
            16,
            Math.round(mobile ? 12 + (window.visualViewport?.offsetTop || 0) : 72)
          ),
          right: 12,
        });
        return;
      }
      setPos({
        top: Math.round(rect.bottom + 8),
        right: Math.max(12, Math.round(window.innerWidth - rect.right)),
      });
    };

    update();
    const t = window.setTimeout(update, 50);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.classList.remove('kkt-dialog-open');
      window.clearTimeout(t);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, anchorRef, handleClose]);

  if ((!mounted && !opened) || typeof document === 'undefined') return null;

  const handleMarkAll = () => {
    setNotifications(markAllRead(notifications));
    showToast('Прочитано', 'ok', 'Все уведомления отмечены');
  };

  const handleClear = () => {
    setNotifications(clearNotifications());
    showToast('Очищено', 'ok', 'Список уведомлений пуст');
  };

  const handleOpen = (n) => {
    setNotifications(markRead(notifications, n.id));
    setOpened(n);
    onClose?.();
  };

  return createPortal(
    <>
      {mounted ? (
        <>
          <button
            ref={backdropRef}
            type="button"
            className={`kkt-blur-backdrop${active ? ' is-visible' : ''}`}
            aria-label="Закрыть уведомления"
            onClick={handleClose}
          />
          <div
            ref={sheetRef}
            className={`notifications-popover${active ? ' is-visible' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notif-title"
            style={isMobile ? undefined : { top: pos.top, right: pos.right }}
          >
            <div
              ref={handleRef}
              className="notifications-sheet__handle"
              aria-hidden="true"
            >
              <span className="notifications-sheet__grabber" />
            </div>
            <div className="notifications-sheet__head">
              <h3 id="notif-title">Уведомления</h3>
              <div className="notifications-sheet__actions">
                {notifications.length > 0 && notifications.some((n) => !n.read) ? (
                  <button
                    type="button"
                    className="notif-icon-btn"
                    onClick={handleMarkAll}
                    aria-label="Прочитать все"
                    title="Прочитать все"
                  >
                    <i className="bi bi-check2-all" aria-hidden="true" />
                  </button>
                ) : null}
                {notifications.length > 0 ? (
                  <button
                    type="button"
                    className="notif-icon-btn notif-icon-btn--danger"
                    onClick={handleClear}
                    aria-label="Очистить"
                    title="Очистить"
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="notifications-sheet__close"
                  onPointerDown={(event) => activateClose(event, handleClose)}
                  onClick={(event) => activateClose(event, handleClose)}
                  aria-label="Закрыть"
                  title="Закрыть"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            {notifications.length === 0 ? (
              <div className="notif-empty">
                <i className="bi bi-bell-slash"></i>
                <p>Пока нет уведомлений</p>
              </div>
            ) : (
              <ul className="notif-list">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`kkt-notice notif-plate ${noticeToneClass(n.level, n.type)} ${n.read ? 'is-read' : 'is-unread'}`}
                      onClick={() => handleOpen(n)}
                    >
                      <span className="kkt-notice__ava" aria-hidden="true">
                        <i className={`bi ${noticeIconClass(n.icon, n.type, n.level)}`} />
                      </span>
                      <span className="kkt-notice__copy">
                        <span className="kkt-notice__head">
                          <strong>{n.title}</strong>
                          <time>{formatNotifTime(n.createdAt)}</time>
                        </span>
                        {n.body ? <span className="kkt-notice__text">{n.body}</span> : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
      <NoticeDialog
        open={Boolean(opened)}
        notice={opened}
        onClose={() => setOpened(null)}
        onOpenLink={(n) => {
          openNoticeLink(n.link, LINKS.eo);
          setOpened(null);
        }}
      />
    </>,
    document.body
  );
};

export default NotificationsPanel;
